/**
 * Retry a tile that failed to load, with backoff.
 *
 * Ported from the main product's `TileRetryHandler`
 * (`frontend/src/components/ui/ThrottledTileLayer.tsx`) with ONE change, and the change is
 * the whole point: there the handler is bound to the MAP, so it catches `tileerror` from
 * every layer — including the Earth Engine one. Each failed Sentinel tile then becomes up to
 * three extra GEE renders, and it happens exactly when GEE is already answering with errors,
 * i.e. when it is already under load. Multiplying the bill by four at the worst possible
 * moment is not a retry policy.
 *
 * Here it binds to ONE layer, and it is attached to the basemap only. Retrying a free
 * provider tile is a courtesy; retrying a billed one is a decision nobody made.
 */

import type * as L from "leaflet";

export interface TileRetryOptions {
  maxRetries?: number;
  baseDelay?: number;
}

interface TileErrorEvent {
  tile?: HTMLImageElement & { _src?: string };
}

/** Returns the detach function. */
export const attachTileRetry = (
  layer: L.TileLayer,
  { maxRetries = 3, baseDelay = 1500 }: TileRetryOptions = {},
): (() => void) => {
  const attempts = new WeakMap<HTMLImageElement, number>();
  const timers = new Set<ReturnType<typeof setTimeout>>();

  const onTileError = (event: unknown): void => {
    const tile = (event as TileErrorEvent).tile;
    const src = tile?._src ?? tile?.src;
    if (!tile || !src) return;

    const attempt = (attempts.get(tile) ?? 0) + 1;
    if (attempt > maxRetries) return;
    attempts.set(tile, attempt);

    const timer = setTimeout(
      () => {
        timers.delete(timer);
        tile.src = "";
        tile.src = src;
      },
      baseDelay * Math.pow(2, attempt - 1),
    );
    timers.add(timer);
  };

  layer.on("tileerror", onTileError);
  return () => {
    layer.off("tileerror", onTileError);
    for (const timer of timers) clearTimeout(timer);
    timers.clear();
  };
};
